import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { recordTriggeredHint } from '../actions/user.actions';
import { selectTriggeredHints } from '../reducers/user.selectors';
import { useAppDispatch } from '../store/hooks';

/**
 * Server-backed onboarding-hint state, replacing per-device localStorage.
 *
 * `hasSeen` reflects the recorded-hint list on the Mongo user object (delivered
 * via `GET /me`). `record()` persists that the hint was triggered — optimistically,
 * so a hint never double-fires within a session, and idempotently, so repeat
 * calls are no-ops. On a failed record the optimistic state is kept (the next
 * `fetchMe` reconciles); we never roll back and re-nag the user.
 */
export function useTriggeredHint(key: string): {
	hasSeen: boolean;
	record: () => void;
} {
	const triggeredHints = useSelector(selectTriggeredHints);
	const dispatch = useAppDispatch();

	const hasSeen = triggeredHints.includes(key);

	const record = useCallback(() => {
		if (hasSeen) {
			return;
		}
		dispatch(recordTriggeredHint(key));
	}, [hasSeen, key, dispatch]);

	return { hasSeen, record };
}

export default useTriggeredHint;
